import { Avatar } from '@mantine/core';
import classNames from 'classnames';
import capitalize from 'lodash.capitalize';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, TTDCampaign } from '@feathr/blackbox';
import { isTTDCampaignWithObjective } from '@feathr/blackbox';
import { Pill, Text, Tooltip } from '@feathr/components';
import type { IObjectiveOptionWithIcon } from '@feathr/extender/hooks';
import { useObjectiveUtils } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import { campaignStateColorMap } from '../../styles/campaign';
import CampaignTypeChip from '../CampaignTypeChip';

import styles from './CampaignChips.css';

interface ICampaignChipsProps {
  campaign: Campaign;
  context?: 'table' | 'header';
}

function CampaignChips({
  campaign,
  context = 'header',
}: Readonly<ICampaignChipsProps>): JSX.Element {
  const { t } = useTranslation();
  const { Targetings } = useStore();
  const { createSelectOptions } = useObjectiveUtils();

  const cls = campaign.get('_cls');
  const state = campaign.get('state');

  let objectives: IObjectiveOptionWithIcon[] = [];
  if (isTTDCampaignWithObjective(cls)) {
    const TTDCampaign = campaign as TTDCampaign;
    objectives = createSelectOptions(TTDCampaign.getAvailableObjectives());
  }
  const objective = objectives.find((objective) => objective.id === campaign.get('objective'));

  // TODO: When we add meta and ctv, we'll need to get them too
  const targetings = Targetings.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
      kind__in: ['omni_lookalike'],
    },
    pagination: { page_size: 100 },
  });

  const hasCTV = false;
  const hasMeta = false;
  const hasLookalike = targetings.models.length > 0;

  // We will only show a maximum of 4 icons here. If there are 5 or more we will show the
  // first 3 and then a +2 icon
  const hasAdditionalTargets = false;

  const hasTargets = hasCTV || hasMeta || hasLookalike;

  return (
    <div className={classNames(styles.root, { [styles.table]: context === 'table' })}>
      <div className={styles.chips}>
        <CampaignTypeChip cls={cls} isMonetization={campaign.isMonetization} />
        <Pill
          name={'campaign-status'}
          theme={campaignStateColorMap.get(state)}
          variant={'secondary'}
        >
          {capitalize(state)}
        </Pill>
      </div>
      {objective && (
        <Text.DefaultRegular className={styles.objective}>{objective.name}</Text.DefaultRegular>
      )}
      {hasTargets && (
        <Avatar.Group className={styles.avatarGroup} spacing={'6'}>
          {hasMeta && (
            <Tooltip title={t('Meta')}>
              <Avatar alt={t('Meta')} color={'sky.6'} size={'24'} variant={'filled'}>
                {getIconForAction('meta')}
              </Avatar>
            </Tooltip>
          )}
          {hasCTV && (
            <Tooltip title={t('CTV')}>
              <Avatar alt={t('CTV')} color={'cyan.6'} size={'24'} variant={'filled'}>
                {getIconForAction('tv')}
              </Avatar>
            </Tooltip>
          )}
          {hasLookalike && (
            <Tooltip title={t('Lookalike')}>
              <Avatar alt={t('Lookalike')} color={'yellow.6'} size={'24'} variant={'filled'}>
                {getIconForAction('lookalike')}
              </Avatar>
            </Tooltip>
          )}
          {/* When there are more than 3 targets */}
          {hasAdditionalTargets && (
            // Not translating since this is placeholder text for additional targets since we don't know what they are yet
            <Tooltip title={'Additional target 1, Additional target 2'}>
              <Avatar color={'slate.4'} size={'24'} variant={'filled'}>
                +2
              </Avatar>
            </Tooltip>
          )}
        </Avatar.Group>
      )}
    </div>
  );
}

export default observer(CampaignChips);
